import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Copy, Check, Plug, Loader2 } from 'lucide-react'
import { AimeLogo } from '@/components/AimeLogo'
import { useSession, useSignIn } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'

type Consent = {
  client_name: string
  redirect_uri: string
  manual_available: boolean
  email: string
}
type Connection = { id: string; client_name: string; created_at: string }
async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch('/api/mcp' + path, {
    credentials: 'include',
    ...options,
    headers: { 'Content-Type': 'application/json' },
  })
  const data = await response.json()
  if (!response.ok)
    throw Error(
      typeof data.error === 'string'
        ? data.error
        : 'Unable to complete this request. Please try again.',
    )
  return data
}
function CopyValue({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState(false)
  return (
    <div className="space-y-2">
      <pre className="bg-surface-container-low overflow-x-auto rounded-xl p-4 text-xs break-all whitespace-pre-wrap">
        {value}
      </pre>
      <Button
        variant="outline"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value)
            setCopied(true)
            setError(false)
          } catch {
            setError(true)
          }
        }}
      >
        {copied ? <Check className="mr-2 size-4" /> : <Copy className="mr-2 size-4" />}
        {copied ? 'Copied' : label}
      </Button>
      {error && (
        <p role="status" className="text-sm">
          Select and copy the text above. Clipboard access is unavailable.
        </p>
      )}
    </div>
  )
}
export default function McpConnectPage() {
  const [params] = useSearchParams(),
    id = params.get('request')
  const { data: session, isLoading } = useSession()
  const signIn = useSignIn(),
    qc = useQueryClient()
  const [email, setEmail] = useState(''),
    [password, setPassword] = useState('')
  const [manual, setManual] = useState(false)
  const [result, setResult] = useState<{
    command?: string
    redirect: string
    denied?: boolean
  } | null>(null)
  const consent = useQuery({
    queryKey: ['mcp-consent', id],
    queryFn: () => request<Consent>('/consent?request=' + encodeURIComponent(id!)),
    enabled: !!session?.user && !!id,
    retry: false,
  })
  const connections = useQuery({
    queryKey: ['mcp-connections'],
    queryFn: () => request<Connection[]>('/connections'),
    enabled: !!session?.user && !id,
  })
  const decide = useMutation({
    mutationFn: (approve: boolean) =>
      request<{ redirect: string; command?: string }>('/consent', {
        method: 'POST',
        body: JSON.stringify({ request: id, approve, manual }),
      }),
    onSuccess: (data, approve) => {
      if (data.command)
        setResult({ command: data.command, redirect: data.redirect, denied: !approve })
      else window.location.assign(data.redirect)
    },
  })
  const revoke = useMutation({
    mutationFn: (connectionId: string) =>
      request('/connections/' + connectionId, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mcp-connections'] }),
  })
  return (
    <main className="bg-background text-foreground min-h-dvh px-6 py-10 sm:py-16">
      <div className="mx-auto max-w-xl space-y-8">
        <Link
          to="/settings"
          className="text-on-surface-variant inline-flex items-center gap-2 text-sm"
        >
          <ArrowLeft className="size-4" />
          Back to AIME
        </Link>
        <AimeLogo size={80} />
        <header className="space-y-3">
          <p className="text-primary text-sm font-semibold tracking-wide">AIME + YOUR ASSISTANT</p>
          <h1 className="text-3xl font-semibold tracking-tight">
            {id ? 'Connect your relationships.' : 'Your CRM, wherever you think.'}
          </h1>
          <p className="text-on-surface-variant">
            {id
              ? 'Give your assistant access to your AIME account.'
              : 'Connect Claude or Codex to find contacts, log interactions, and keep your relationships moving.'}
          </p>
        </header>
        {isLoading ? (
          <p role="status" className="flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" />
            Loading your account…
          </p>
        ) : !session?.user ? (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              signIn.mutate({ email, password })
            }}
          >
            <h2 className="text-lg font-semibold">Sign in to AIME</h2>
            <label className="block space-y-1 text-sm">
              <span>Email</span>
              <input
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-background block w-full rounded-xl border p-3"
              />
            </label>
            <label className="block space-y-1 text-sm">
              <span>Password</span>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-background block w-full rounded-xl border p-3"
              />
            </label>
            {signIn.error && (
              <p role="alert" className="text-destructive text-sm">
                {signIn.error.message}
              </p>
            )}
            <Button type="submit" disabled={signIn.isPending}>
              {signIn.isPending ? 'Signing in…' : 'Sign in'}
            </Button>
            <p className="text-sm">
              <Link to="/forgot-password" className="text-primary underline">
                Forgot password?
              </Link>
            </p>
          </form>
        ) : id ? (
          <section className="space-y-6">
            {consent.isPending && <p role="status">Loading connection request…</p>}
            {consent.error && (
              <p role="alert" className="text-destructive">
                {consent.error.message}
              </p>
            )}
            {result ? (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">
                  {result.denied ? 'Access declined' : 'Finish connecting your assistant'}
                </h2>
                <p>
                  If your assistant asks you to paste a redirect URL, copy this URL back into that
                  prompt. It expires after 10 minutes.
                </p>
                <CopyValue value={result.redirect} label="Copy callback URL" />
                <details className="text-sm">
                  <summary className="cursor-pointer font-medium">
                    My assistant is waiting for a local callback
                  </summary>
                  <p className="my-3">
                    For clients such as Codex that keep a callback server running, run this command
                    in another terminal on the machine where you started login.
                  </p>
                  <CopyValue value={result.command!} label="Copy callback command" />
                </details>
                <p className="text-on-surface-variant text-sm">
                  Keep these values private. They contain your single-use authorization code.
                </p>
              </div>
            ) : (
              consent.data && (
                <>
                  <div className="space-y-2 border-y py-5">
                    <h2 className="text-xl font-semibold">
                      {consent.data.client_name} wants to connect
                    </h2>
                    <p className="text-sm break-all">Account: {consent.data.email}</p>
                    <p className="text-on-surface-variant text-xs break-all">
                      Requested return address: {consent.data.redirect_uri}
                    </p>
                  </div>
                  <div className="space-y-3">
                    <h3 className="font-semibold">Full access to your CRM</h3>
                    <ul className="list-disc space-y-2 pl-5 text-sm">
                      <li>
                        Read, search, create, edit, and delete contacts, circles, labels, notes,
                        interactions, reminders, gifts, and to-dos.
                      </li>
                      <li>
                        Import and export data, manage files and connected accounts, and use your
                        available AI features.
                      </li>
                      <li>
                        Share contacts, send introductions, and manage your billing through the same
                        actions available in AIME.
                      </li>
                    </ul>
                    <p className="text-on-surface-variant text-sm">
                      Only connect an assistant you trust. Your plan limits still apply, and you can
                      revoke access at any time.
                    </p>
                  </div>
                  {consent.data.manual_available && (
                    <label className="flex items-start gap-3 rounded-xl border p-4 text-sm">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={manual}
                        onChange={(e) => setManual(e.target.checked)}
                      />
                      <span>
                        <strong className="block">My assistant is on another machine</strong>Show a
                        callback URL or command I can copy into its terminal.
                      </span>
                    </label>
                  )}
                  {decide.error && (
                    <p role="alert" className="text-destructive">
                      {decide.error.message}
                    </p>
                  )}
                  <div className="flex gap-3">
                    <Button disabled={decide.isPending} onClick={() => decide.mutate(true)}>
                      {decide.isPending ? 'Connecting…' : 'Allow access'}
                    </Button>
                    <Button
                      variant="outline"
                      disabled={decide.isPending}
                      onClick={() => decide.mutate(false)}
                    >
                      Decline
                    </Button>
                  </div>
                </>
              )
            )}
          </section>
        ) : (
          <>
            <section className="space-y-4">
              <h2 className="text-xl font-semibold">Connect an assistant</h2>
              <CopyValue value={window.location.origin + '/mcp'} label="Copy MCP URL" />
              <p className="text-sm">
                In Claude, open Settings → Connectors → Add custom connector. In Codex, open
                Settings → MCP servers and add a Streamable HTTP server. Use this URL, then sign in
                and approve access.
              </p>
              <details className="text-sm">
                <summary className="cursor-pointer font-medium">Connect from a terminal</summary>
                <div className="mt-3">
                  <CopyValue
                    value={
                      'codex mcp add aime --url ' +
                      window.location.origin +
                      '/mcp\ncodex mcp login aime'
                    }
                    label="Copy commands"
                  />
                </div>
                <p className="mt-3">
                  For a remote terminal, open the login link on another device and select “My
                  assistant is on another machine.” Paste the callback URL when prompted, or run the
                  callback command if your client is waiting for a local callback.
                </p>
              </details>
            </section>
            <section className="space-y-4 border-t pt-6">
              <h2 className="text-xl font-semibold">Connected assistants</h2>
              {connections.isPending && <p role="status">Loading connections…</p>}
              {connections.error && (
                <p role="alert" className="text-destructive">
                  {connections.error.message}
                </p>
              )}
              {connections.data?.length === 0 && (
                <p className="text-on-surface-variant text-sm">
                  No assistants connected yet. Add the MCP URL to Claude or Codex to get started.
                </p>
              )}
              {connections.data?.map((connection) => (
                <div
                  key={connection.id}
                  className="flex items-center justify-between gap-4 border-b py-4"
                >
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 font-medium">
                      <Plug className="size-4 shrink-0" />
                      <span className="break-all">{connection.client_name}</span>
                    </p>
                    <p className="text-on-surface-variant mt-1 text-xs">
                      Connected {new Date(connection.created_at).toLocaleDateString()} · Full CRM
                      access
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(connection.id)}
                  >
                    Revoke
                  </Button>
                </div>
              ))}
              {revoke.error && (
                <p role="alert" className="text-destructive">
                  {revoke.error.message}
                </p>
              )}
            </section>
          </>
        )}
      </div>
    </main>
  )
}
